'use client';
import { useEffect, useRef, useState, useCallback } from 'react';
import createGlobe, { COBEOptions } from 'cobe';
import { motion } from 'motion/react';

// Infrastructure locations with impact data
const INFRASTRUCTURE_MARKERS = [
  // Power & Energy
  { location: [51.5074, -0.1278], size: 0.08, label: 'London Grid', impact: '£2.8M/hr' }, // London
  { location: [40.7128, -74.006], size: 0.1, label: 'NYC Power Hub', impact: '£3.2M/hr' }, // NYC
  { location: [35.6762, 139.6503], size: 0.09, label: 'Tokyo Systems', impact: '£2.1M/hr' }, // Tokyo
  { location: [22.3193, 114.1694], size: 0.07, label: 'HK Financial', impact: '£1.9M/hr' }, // Hong Kong
  { location: [1.3521, 103.8198], size: 0.06, label: 'Singapore Hub', impact: '£1.4M/hr' }, // Singapore
  // Oil & Gas
  { location: [25.2048, 55.2708], size: 0.08, label: 'Dubai Refinery', impact: '£4.1M/hr' }, // Dubai
  { location: [29.7604, -95.3698], size: 0.09, label: 'Houston Ops', impact: '£5.2M/hr' }, // Houston
  { location: [59.9139, 10.7522], size: 0.06, label: 'Oslo Platform', impact: '£2.3M/hr' }, // Oslo
  // Manufacturing
  { location: [31.2304, 121.4737], size: 0.08, label: 'Shanghai Plant', impact: '£1.8M/hr' }, // Shanghai
  { location: [48.1351, 11.582], size: 0.07, label: 'Munich Factory', impact: '£1.5M/hr' }, // Munich
  { location: [37.5665, 126.978], size: 0.07, label: 'Seoul Tech', impact: '£1.7M/hr' }, // Seoul
  // Data Centers
  { location: [47.6062, -122.3321], size: 0.06, label: 'Seattle DC', impact: '£890K/hr' }, // Seattle
  { location: [52.52, 13.405], size: 0.06, label: 'Berlin DC', impact: '£760K/hr' }, // Berlin
  { location: [-33.8688, 151.2093], size: 0.05, label: 'Sydney Node', impact: '£620K/hr' }, // Sydney
];

interface GlobeProps {
  className?: string;
  onMarkerHover?: (marker: typeof INFRASTRUCTURE_MARKERS[0] | null) => void;
}

export default function Globe({ className = '', onMarkerHover }: GlobeProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pointerInteracting = useRef<number | null>(null);
  const pointerInteractionMovement = useRef(0);
  const phiRef = useRef(0);
  const widthRef = useRef(0);
  const globeRef = useRef<ReturnType<typeof createGlobe> | null>(null);
  const [isHovering, setIsHovering] = useState(false);

  // Convert lat/lng to phi/theta for marker detection
  const latLngToAngles = useCallback((lat: number, lng: number) => {
    return {
      phi: (90 - lat) * (Math.PI / 180),
      theta: (lng + 180) * (Math.PI / 180),
    };
  }, []);

  useEffect(() => {
    let width = 0;
    const onResize = () => {
      if (canvasRef.current) {
        width = canvasRef.current.offsetWidth;
        widthRef.current = width;
      }
    };
    window.addEventListener('resize', onResize);
    onResize();

    if (!canvasRef.current) return;

    const globe = createGlobe(canvasRef.current, {
      devicePixelRatio: 2,
      width: width * 2,
      height: width * 2,
      phi: 0,
      theta: 0.25,
      dark: 0,
      diffuse: 1.2,
      mapSamples: 20000,
      mapBrightness: 6,
      baseColor: [0.96, 0.96, 0.94], // Warm off-white
      markerColor: [0.92, 0.62, 0.15], // Amber color from theme
      glowColor: [0.98, 0.98, 0.96],
      markers: INFRASTRUCTURE_MARKERS.map((m) => ({
        location: m.location as [number, number],
        size: m.size,
      })),
    });

    globeRef.current = globe;

    let rafId: number;
    const animate = () => {
      if (!pointerInteracting.current) {
        phiRef.current += 0.003;
      }
      globe.update({
        phi: phiRef.current + pointerInteractionMovement.current,
        width: width * 2,
        height: width * 2,
      });
      rafId = requestAnimationFrame(animate);
    };
    rafId = requestAnimationFrame(animate);

    // Fade in animation
    if (canvasRef.current) {
      canvasRef.current.style.opacity = '0';
      setTimeout(() => {
        if (canvasRef.current) {
          canvasRef.current.style.opacity = '1';
        }
      }, 100);
    }

    return () => {
      cancelAnimationFrame(rafId);
      globe.destroy();
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Pointer event handlers for interactivity
  const handlePointerDown = (e: React.PointerEvent) => {
    pointerInteracting.current = e.clientX - pointerInteractionMovement.current;
    if (canvasRef.current) {
      canvasRef.current.style.cursor = 'grabbing';
    }
  };

  const handlePointerUp = () => {
    pointerInteracting.current = null;
    if (canvasRef.current) {
      canvasRef.current.style.cursor = 'grab';
    }
  };

  const handlePointerOut = () => {
    pointerInteracting.current = null;
    setIsHovering(false);
    if (canvasRef.current) {
      canvasRef.current.style.cursor = 'grab';
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (pointerInteracting.current !== null) {
      const delta = e.clientX - pointerInteracting.current;
      pointerInteractionMovement.current = delta / 100;
    }
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  return (
    <div className={`relative aspect-square ${className}`}>
      {/* Glow effect behind globe */}
      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[hsl(var(--amber))]/20 via-transparent to-[hsl(var(--teal))]/10 blur-3xl scale-110" />

      {/* Outer ring */}
      <motion.div
        className="absolute inset-0 rounded-full border border-[hsl(var(--amber))]/20"
        animate={{
          scale: [1, 1.02, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* The Globe Canvas */}
      <canvas
        ref={canvasRef}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerOut={handlePointerOut}
        onPointerMove={handlePointerMove}
        onMouseEnter={handleMouseEnter}
        className="w-full h-full cursor-grab transition-opacity duration-1000"
        style={{
          contain: 'layout paint size',
          willChange: 'transform',
        }}
      />

      {/* Hover instruction */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovering ? 0 : 1 }}
        transition={{ delay: 2, duration: 0.5 }}
        className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-[hsl(var(--slate-medium))] bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full border border-[hsl(var(--border))]"
      >
        Drag to explore
      </motion.div>
    </div>
  );
}

// Export markers for use in parent components
export { INFRASTRUCTURE_MARKERS };
